import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { initialDemoContent } from '@/data/demo-content'
import type { DemoContent, Question, QuestionSection, QuestionType } from '@/types/content'

const STORAGE_KEY = 'meta-math-demo-content-v1'

function cloneInitialContent(): DemoContent {
  return structuredClone(initialDemoContent)
}

function isCompatibleContent(value: unknown): value is DemoContent {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<DemoContent>
  return (
    candidate.version === initialDemoContent.version &&
    Array.isArray(candidate.pretest) &&
    Array.isArray(candidate.mainTest) &&
    candidate.pretest.length > 0 &&
    candidate.mainTest.length > 0
  )
}

function createBlankQuestion(section: QuestionSection, type?: QuestionType): Question {
  const resolvedType = type ?? (section === 'pretest' ? 'PRE' : 'A')
  const idPrefix = section === 'pretest' ? 'pre' : `main-${resolvedType.toLowerCase()}`
  return {
    id: `${idPrefix}-${Date.now()}`,
    enabled: true,
    section,
    type: resolvedType,
    prompt: '새 문제를 입력하세요.',
    instruction: resolvedType === 'A' || resolvedType === 'B' ? '문제를 보고 생각해 보세요.' : '',
    visualType: 'none',
    timerSeconds: resolvedType === 'C' ? 30 : 10,
    thinkSeconds: resolvedType === 'A' || resolvedType === 'B' ? 10 : undefined,
    answerSeconds: resolvedType === 'A' || resolvedType === 'B' ? 10 : undefined,
    timeoutChoiceId: '__last__',
    choices: [
      { id: 'choice-1', text: '선택지 1' },
      { id: 'choice-2', text: '모르겠습니다' },
    ],
  }
}

export const useContentStore = defineStore('content', () => {
  const content = ref<DemoContent>(cloneInitialContent())

  const activePretest = computed(() => content.value.pretest.filter((item) => item.enabled))
  const activeMainTest = computed(() => content.value.mainTest.filter((item) => item.enabled))

  function list(section: QuestionSection): Question[] {
    return section === 'pretest' ? content.value.pretest : content.value.mainTest
  }

  function loadFromBrowser(): void {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    try {
      const parsed: unknown = JSON.parse(raw)
      if (isCompatibleContent(parsed)) {
        content.value = parsed
        return
      }
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  function saveToBrowser(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(content.value))
  }

  function replaceContent(next: DemoContent): void {
    content.value = structuredClone(next)
    saveToBrowser()
  }

  function addQuestion(section: QuestionSection, type?: QuestionType): Question {
    const question = createBlankQuestion(section, type)
    list(section).push(question)
    return question
  }

  function duplicateQuestion(section: QuestionSection, id: string): Question | null {
    const items = list(section)
    const index = items.findIndex((item) => item.id === id)
    if (index < 0) return null
    const copy = structuredClone(items[index])
    copy.id = `${copy.id}-copy-${Date.now().toString().slice(-5)}`
    items.splice(index + 1, 0, copy)
    return copy
  }

  function deleteQuestion(section: QuestionSection, id: string): void {
    const items = list(section)
    const index = items.findIndex((item) => item.id === id)
    if (index >= 0) items.splice(index, 1)
  }

  function moveQuestion(section: QuestionSection, fromIndex: number, toIndex: number): void {
    const items = list(section)
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length || toIndex >= items.length) return
    const [item] = items.splice(fromIndex, 1)
    items.splice(toIndex, 0, item)
  }

  function reset(): void {
    content.value = cloneInitialContent()
    localStorage.removeItem(STORAGE_KEY)
  }

  return {
    content,
    activePretest,
    activeMainTest,
    list,
    loadFromBrowser,
    saveToBrowser,
    replaceContent,
    addQuestion,
    duplicateQuestion,
    deleteQuestion,
    moveQuestion,
    reset,
  }
})
